import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, CornerDownLeft } from "lucide-react";
import { api, mxn } from "@/lib/api";

const TYPE_META = {
  cotizacion: { label: "Cotización", color: "#465B7F" },
  remision: { label: "Remisión", color: "#49AC9A" },
  orden: { label: "OT", color: "#E0A93B" },
  factura: { label: "Factura", color: "#ED1F26" },
};

export function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setQ("");
    setResults([]);
    setActive(0);
  }, []);

  // Atajo de teclado Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  // Búsqueda con debounce
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 1) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get(`/search?q=${encodeURIComponent(term)}`)
        .then((r) => {
          setResults(r.data || []);
          setActive(0);
        })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const go = (item) => {
    if (!item) return;
    close();
    navigate(item.path);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="global-search-trigger"
        title="Buscar (Ctrl/Cmd + K)"
        className="flex items-center gap-3 py-3 w-full text-base font-bold text-white/80 hover:bg-white/10 hover:text-white transition-colors justify-center group-hover:justify-start px-0 group-hover:px-6"
      >
        <Search size={20} strokeWidth={1.75} className="shrink-0" />
        <span className="hidden group-hover:inline truncate">Buscar</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4 bg-black/50 backdrop-blur-sm"
          data-testid="global-search-modal"
          onClick={close}
        >
          <div
            className="w-full max-w-2xl bg-white dark:bg-[#0F2138] border border-[#E3E8EE] dark:border-[#1D3454] shadow-2xl rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 border-b border-[#E3E8EE] dark:border-[#1D3454]">
              <Search size={20} className="text-[#5B6672] shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                data-testid="global-search-input"
                placeholder="Buscar por número o cliente (cotización, remisión, OT, factura)…"
                className="flex-1 py-4 bg-transparent outline-none font-mono-data text-base text-[#16202B] dark:text-[#E7EDF6] placeholder:text-[#98A2B3]"
              />
              <button onClick={close} className="text-[#5B6672] hover:text-[#16202B] dark:hover:text-white shrink-0">
                <X size={20} />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-auto">
              {loading && (
                <div className="px-5 py-6 font-mono-data text-sm text-[#5B6672]" data-testid="global-search-loading">
                  Buscando…
                </div>
              )}
              {!loading && q.trim().length >= 1 && results.length === 0 && (
                <div className="px-5 py-6 font-mono-data text-sm text-[#5B6672]" data-testid="global-search-empty">
                  Sin resultados para “{q.trim()}”.
                </div>
              )}
              {!loading && q.trim().length === 0 && (
                <div className="px-5 py-6 font-mono-data text-sm text-[#98A2B3]">
                  Escribe un número de documento o el nombre de un cliente.
                </div>
              )}
              {!loading &&
                results.map((item, i) => {
                  const meta = TYPE_META[item.type] || { label: item.type, color: "#5B6672" };
                  return (
                    <button
                      key={`${item.type}-${item.id}`}
                      onClick={() => go(item)}
                      onMouseEnter={() => setActive(i)}
                      data-testid={`global-search-result-${item.type}-${item.number}`}
                      className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors border-b border-[#F0F3F7] dark:border-[#16273F] last:border-0 ${
                        i === active ? "bg-[#E9EEF3] dark:bg-[#16273F]" : "hover:bg-[#F5F8FB] dark:hover:bg-[#132339]"
                      }`}
                    >
                      <span
                        className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-white px-2 py-1 rounded"
                        style={{ backgroundColor: meta.color }}
                      >
                        {meta.label}
                      </span>
                      <span className="font-mono-data text-sm font-semibold text-[#16202B] dark:text-[#E7EDF6] shrink-0">
                        {item.number}
                      </span>
                      <span className="font-mono-data text-sm text-[#5B6672] truncate flex-1">{item.client_name}</span>
                      <span className="font-mono-data text-sm text-[#16202B] dark:text-[#E7EDF6] shrink-0">
                        {mxn(item.total)}
                      </span>
                    </button>
                  );
                })}
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-[#E3E8EE] dark:border-[#1D3454] bg-[#F5F8FB] dark:bg-[#0A1626]">
              <span className="font-mono-data text-xs text-[#98A2B3] flex items-center gap-1">
                <CornerDownLeft size={13} /> para abrir · Esc para cerrar
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
